/* eslint-disable @web/i18n-jsx-attribute-no-literals */
/* oxlint-disable react-js/jsx-no-literals */

// Note: since this banner is only to be shown in the US, we are not extracting
// any strings for translation.

/**
 * NOTE: The design and copy of our cookie banners has been fixed and approved by our legal team.
 *       Please reach out to one of our product counsels if you need to modify it in any way.
 */

import React, { type JSX } from 'react'
import { t } from '@figma/i18n/translation'
import { cookiesDismiss } from '@figma/i18n-dictionaries/en/web/cookies'
import { isInIframe } from '@web/lib/browser'
import { EMPTY_SIZE, useResizeObserver } from '@web/lib/hooks/resize_observer'
import { useAtomValue, useSetAtom } from '@web/lib/jotai'
import platform from '@web/lib/platform'
import { DEFAULT_CONTEXT_CTA_CLICKED_EVENT_NAME } from '@web/o11y/analytics/tracking'
import { TrackingContext, useTrackingReactContext } from '@web/o11y/analytics/tracking_context'
import { Svg } from '@web/ui-platform/svg_deprecated'
import classNames from 'classnames'

import { CtaTrackingDescriptors } from 'lib/analytics/cta_tracking_descriptors'
import initialOptions from 'lib/initial_options'
import { AnchorElementTracked } from 'shared/components/forms/basic_form/basic_form'

import { isMeetDevice } from 'figma_app/lib/fullscreen/fullscreen_integration_helpers'

import {
  cookieBannerHeightAtom,
  CookieExistingPreferences,
  cookieExistingPreferencesAtom,
  setCookiePreferences,
} from './cookie_banner_atoms'
import { getGlobalPrivacyControl } from './global_privacy_control'
import { CookieBannerVariant } from './types'

import * as styles from './base_cookie_banner.css'

const AUTOMATIC_OPT_IN_DURATION_MS = 10000

type Props = {
  dismissIcon: SvgSrc
  SecondaryButton: React.ElementType
  variant: CookieBannerVariant
  persistentMessage?: JSX.Element
  verticalPadding?: number
}

export function CookieBannerImplicitInner(props: Props & { showBanner: boolean }) {
  const [isShowing, setIsShowing] = React.useState(props.showBanner)
  const [hasClickedOutside, setHasClickedOutside] = React.useState(false)
  const bannerContainerElement = React.useRef<HTMLDivElement>(null)
  const timeoutId = React.useRef<number>(undefined)
  const setHeight = useSetAtom(cookieBannerHeightAtom)

  const cookieBannerSize = useResizeObserver(bannerContainerElement) ?? EMPTY_SIZE
  const newCookieBannerHeight = isShowing
    ? cookieBannerSize.height + (props.verticalPadding || 0)
    : 0

  React.useEffect(() => {
    setHeight(newCookieBannerHeight)
  }, [newCookieBannerHeight, setHeight])

  // We want to utilize any context we have available from the parent tracking context,
  // BUT we don't want to re-generate any callbacks unnecessarily based on any updates
  // from the `useContext` call below.
  //
  // As such, we're opting to use a ref to hold a mutable version of the trackEvent call.
  // We only care about it being accurate enough at the moment the callback occurs, and
  // using a ref will ensure that's the case
  //
  // We only need to do this for the Implicit cookie banner because the event handlers
  // we are listening to are global to the document, and as such, require memoized callbacks.
  const { trackEvent } = useTrackingReactContext()
  const trackEventRef = React.useRef<Function>(trackEvent)

  trackEventRef.current = trackEvent

  React.useEffect(() => {
    return () => {
      clearTimeout(timeoutId.current)
    }
  }, [])

  // If the user clicks anywhere outside of the banner,
  // after 10 seconds we automatically opt them into cookies
  const onClick = React.useCallback(
    (event: Event) => {
      const isBannerClick = bannerContainerElement.current?.contains(event.target as Node)
      if (isBannerClick) return

      setHasClickedOutside(true)
      window.removeEventListener('click', onClick)

      timeoutId.current = setTimeout(async () => {
        if (!isShowing) return null
        setIsShowing(false)
        await setCookiePreferences({
          consentRegion: 'implicit',
          cookiesEnabled: true,
        })
        trackEventRef.current(DEFAULT_CONTEXT_CTA_CLICKED_EVENT_NAME, {
          trackingDescriptor: CtaTrackingDescriptors.COOKIE_BANNER_IMPLICIT_CONSENT_DISMISS,
        })
      }, AUTOMATIC_OPT_IN_DURATION_MS)
    },
    [isShowing],
  )

  // If the user has clicked outside of the banner and navigates to
  // another page before 10 seconds has passed, we still want to set the pref
  const onBeforeUnload = React.useCallback(async () => {
    clearTimeout(timeoutId.current)

    if (hasClickedOutside) {
      await setCookiePreferences({
        consentRegion: 'implicit',
        cookiesEnabled: true,
      })
      trackEventRef.current(DEFAULT_CONTEXT_CTA_CLICKED_EVENT_NAME, {
        trackingDescriptor: CtaTrackingDescriptors.COOKIE_BANNER_IMPLICIT_CONSENT_DISMISS,
      })
    }
  }, [hasClickedOutside])

  // Listen to clicks outside of the banner and page navigation
  React.useEffect(() => {
    if (!isShowing) return
    window.addEventListener('click', onClick)
    window.addEventListener('beforeunload', onBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      window.removeEventListener('click', onClick)
    }
  }, [isShowing, onClick, onBeforeUnload])

  const handleClickOptOut = async () => {
    setIsShowing(false)
    window.removeEventListener('click', onClick)
    await setCookiePreferences({ consentRegion: 'implicit', cookiesEnabled: false })
    trackEventRef.current(DEFAULT_CONTEXT_CTA_CLICKED_EVENT_NAME, {
      trackingDescriptor: CtaTrackingDescriptors.COOKIE_BANNER_IMPLICIT_CONSENT_OPT_OUT,
    })
  }
  const handleDismiss = async () => {
    setIsShowing(false)
    window.removeEventListener('click', onClick)
    await setCookiePreferences({ consentRegion: 'implicit', cookiesEnabled: true })
    trackEventRef.current(DEFAULT_CONTEXT_CTA_CLICKED_EVENT_NAME, {
      trackingDescriptor: CtaTrackingDescriptors.COOKIE_BANNER_IMPLICIT_CONSENT_DISMISS,
    })
  }

  if (!isShowing) {
    return (
      (props.persistentMessage && (
        <TrackingContext name="Cookie Banner Persistent Message">
          {props.persistentMessage}
        </TrackingContext>
      )) ||
      null
    )
  }

  return (
    <div
      className={classNames(
        styles.container,
        props.variant === CookieBannerVariant.COMMUNITY && [
          styles.container__authOrCommunity,
          styles.container__community,
        ],
        props.variant === CookieBannerVariant.FILE_VIEWER && styles.container__fileViewer,
        props.variant === CookieBannerVariant.PROTOTYPE && styles.container__prototype,
        props.variant === CookieBannerVariant.AUTH && [
          styles.container__authOrCommunity,
          styles.container__auth,
        ],
      )}
      role="region"
      aria-label="Consent Manager"
      ref={bannerContainerElement}
      data-testid="cookie-banner"
    >
      <div className={styles.body}>
        This website uses cookies, pixel tags, and local storage for performance, personalization,
        and marketing purposes. Our use of some cookies may be considered a sale, sharing for
        behavioral advertising, or targeted advertising. For more, see our{' '}
        <AnchorElementTracked rel="noopener" target="_blank" href="/privacy/">
          privacy policy
        </AnchorElementTracked>
        . <b>California Residents</b> can learn how personal information is{' '}
        <AnchorElementTracked href="/privacy/#ca-notice" rel="noopener" target="_blank">
          collected
        </AnchorElementTracked>
        , including how it is{' '}
        <AnchorElementTracked href="/privacy/#purpose" rel="noopener" target="_blank">
          used
        </AnchorElementTracked>
        , whether it is{' '}
        <AnchorElementTracked href="/privacy/#ca-notice" rel="noopener" target="_blank">
          “sold” or “shared”
        </AnchorElementTracked>
        , and how long it is{' '}
        <AnchorElementTracked href="/privacy/#retention" rel="noopener" target="_blank">
          retained
        </AnchorElementTracked>
        .
      </div>
      <div className={styles.actions}>
        <props.SecondaryButton onClick={handleClickOptOut} data-testid="cookie-opt-out-button">
          Opt out
        </props.SecondaryButton>
      </div>
      <div className={styles.dismiss}>
        <button
          onClick={handleDismiss}
          type="button"
          data-testid="cookie-dismiss-button"
          aria-label={t(cookiesDismiss)}
        >
          <Svg className={styles.dismissIcon} svg={props.dismissIcon} />
        </button>
      </div>
    </div>
  )
}

export function CookieBannerImplicit(props: Props) {
  const hasExistingPreferences = useAtomValue(cookieExistingPreferencesAtom)

  let shouldHideCookieBanner = false

  if (hasExistingPreferences === CookieExistingPreferences.UNLOADED) {
    shouldHideCookieBanner = true
  }

  if (hasExistingPreferences === CookieExistingPreferences.YES && !props.persistentMessage) {
    shouldHideCookieBanner = true
  }

  // Respect the GlobalPrivacyControl browser API
  if (getGlobalPrivacyControl()) {
    shouldHideCookieBanner = true
  }

  // Don't show the cookie banner to logged-in users
  if (initialOptions().user_data?.id) {
    shouldHideCookieBanner = true
  }

  // NEVER show cookie banners if we're in an embed
  if (initialOptions().is_embed || isInIframe()) {
    shouldHideCookieBanner = true
  }

  // On Google Meet devices, we only use essential cookies and clear them when a file is closed
  if (isMeetDevice()) {
    shouldHideCookieBanner = true
  }

  React.useEffect(() => {
    if (!shouldHideCookieBanner && platform.isIpadNative) {
      // Opt out the cookie tracking as we only need "essential" cookies for logged out experience in iPad app.
      setCookiePreferences({ consentRegion: 'explicit', cookiesEnabled: false })
    }
  }, [shouldHideCookieBanner])

  if (shouldHideCookieBanner || platform.isIpadNative) {
    return null
  }

  return (
    <TrackingContext name="Cookie Banner" properties={{ consentRegion: 'implicit' }}>
      <CookieBannerImplicitInner
        {...props}
        showBanner={hasExistingPreferences === CookieExistingPreferences.NO}
      />
    </TrackingContext>
  )
}
